// client/src/services/supportPlatform.ts (TypeScript since ST-00216)
//
// Thin client for the lexadapt-support platform, mounted same-origin at
// /support/ (SPA) and /support-api/ (API) — ST-00143 phase 2. LexiBridge keeps
// only two integration points: the sidebar link into the embedded app
// (SUPPORT_APP_PATH, where "+ New Ticket" logs a ticket) and the sidebar unread
// badge (supportPlatform.unreadCount). ST-00283 removed the floating
// create-ticket button, and with it the create / upload / vocab / products
// calls and the "Open full editor" deep link, which only it used.
//
// Auth: exchanges the LexiBridge cookie session for a short-lived RS256
// Bearer token via POST /api/auth/support-token (aud=lexadapt-support),
// caches it in memory, and re-exchanges once on 401. Nothing is persisted.
// The only call left is the background badge poll, so the exchange never
// refreshes the LexiBridge session (ST-00215 review S5: a poll must not keep an
// idle session alive). A 401 from the exchange itself means the access cookie
// expired; it is reported as `err.sessionExpired`.
//
// ST-00145: the legacy in-app ticket system (and its SUPPORT_TICKETS_LEGACY
// rollback flag) has been deleted — the platform is the only ticketing
// surface LexiBridge has.

import type {
  PlatformFetchFlags,
  SupportPlatformError,
  SupportTokenResponse,
  SupportUnreadCount,
} from '../types/clientShell';

export type { SupportPlatformError };

const EXCHANGE_PATH = '/api/auth/support-token';
const PLATFORM_API = '/support-api/api/v1';

// Hash route: works wherever the SPA is mounted, no server fallback needed.
export const SUPPORT_APP_PATH = '/support/#/tickets';

let cachedToken: string | null = null;

function csrfToken(): string {
  const m = document.cookie.match(/(?:^|;\s*)lb_csrf=([^;]+)/);
  // The capture group always takes part in a match.
  return m ? decodeURIComponent(m[1]!) : '';
}

async function exchangeToken(): Promise<string> {
  const res = await fetch(EXCHANGE_PATH, {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrfToken() },
  });
  if (!res.ok) {
    const err: SupportPlatformError = new Error(`Support token exchange failed (${res.status})`);
    err.status = res.status;
    // 401 here = the LexiBridge session itself has lapsed (not the platform).
    err.sessionExpired = res.status === 401;
    throw err;
  }
  const data = (await res.json()) as SupportTokenResponse;
  cachedToken = data.token;
  return cachedToken;
}

/** GET a platform resource with the exchanged token. */
async function platformGet<T>(path: string, { retried = false }: PlatformFetchFlags = {}): Promise<T> {
  const token = cachedToken || (await exchangeToken());
  const res = await fetch(`${PLATFORM_API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401 && !retried) {
    // Exchange token expired (5-min TTL) — re-exchange once and retry.
    cachedToken = null;
    return platformGet<T>(path, { retried: true });
  }
  const data = (res.status === 204 ? null : await res.json().catch(() => null)) as
    (T & { error?: string }) | null;
  if (!res.ok) {
    const err: SupportPlatformError = new Error(
      data?.error || `Support platform request failed (${res.status})`
    );
    err.status = res.status;
    throw err;
  }
  return data as T;
}

export const supportPlatform = {
  /**
   * -> { count } (open tickets with activity since last viewed).
   * Background call (the sidebar badge poll): it never refreshes the LexiBridge
   * session. When the session has lapsed it rejects with `err.sessionExpired`.
   */
  unreadCount: () => platformGet<SupportUnreadCount>('/tickets/unread-count'),
};
